import { FloatingFormItem } from '@/components/_common';
import { useInvoiceSettingsUploadCertificate } from '@/core/domain/invoice/certificate/Certificate.hooks';
import { useDefaultEmptyFieldError } from '@/hooks';
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  ModalProps,
  Row,
  Upload,
  message,
} from 'antd';
import { useIntl } from 'react-intl';

interface CertificateFormValues {
  certificateEmail: string;
  certificatePassword: string;
  certificateFile: { file: File };
  id?: number;
}

const SettingsFiscalCreateCertificateModal: React.FC<ModalProps> = (props) => {
  const intl = useIntl().formatMessage;

  const [form] = Form.useForm<CertificateFormValues>();

  const rulesRequired = useDefaultEmptyFieldError();

  const uploadCertificate = useInvoiceSettingsUploadCertificate();

  function validateFields(values: CertificateFormValues) {
    if (!values.certificateFile || !values.certificatePassword) {
      if (!values.certificateFile) {
        form.setFields([
          { name: 'certificateFile', errors: ['Selecione um arquivo'] },
        ]);
      }
      throw new Error();
    }
  }

  async function onSubmit(event: React.MouseEvent<HTMLElement, MouseEvent>) {
    try {
      const values = (await form.validateFields([
        'certificateEmail',
        'certificateFile',
        'certificatePassword',
      ])) as CertificateFormValues;

      validateFields(values);

      if (values) {
        const data = {
          email: values.certificateEmail ?? null,
          certificateFile: values.certificateFile.file,
          certificatePassword: values.certificatePassword,
          isEdit: false,
        };

        await uploadCertificate.mutateAsync(data);

        onClose(event);
      }
    } catch {}
  }

  function onClose(event: React.MouseEvent<HTMLElement, MouseEvent>) {
    form.resetFields();
    !!props.onCancel && props.onCancel(event);
  }

  return (
    <Modal
      {...props}
      title="Cadastro de certificado"
      onOk={onSubmit}
      onCancel={onClose}
      confirmLoading={uploadCertificate.isLoading}
    >
      <Form form={form}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Form.Item
              style={{ margin: 0 }}
              name="certificateFile"
              rules={[rulesRequired]}
            >
              <Upload
                accept=".pfx"
                type="select"
                name="file"
                disabled={uploadCertificate.isLoading}
                maxCount={1}
                beforeUpload={(file) => {
                  const isPfx = file.type === 'application/x-pkcs12';

                  if (!isPfx) {
                    message.error(`${file.name} não é um arquivo ".pfx"`);
                  } else {
                    return false;
                  }

                  return isPfx || Upload.LIST_IGNORE;
                }}
              >
                <Button>Selecionar arquivo do certificado</Button>
              </Upload>
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <FloatingFormItem
              name="certificatePassword"
              label={intl({
                id: 'companies.customTerms.invoice.form.certificatePassword',
              })}
              rules={[rulesRequired]}
            >
              <Input disabled={uploadCertificate.isLoading} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12}>
            <FloatingFormItem
              name="certificateEmail"
              label={intl({
                id: 'companies.customTerms.invoice.form.email',
              })}
            >
              <Input disabled={uploadCertificate.isLoading} />
            </FloatingFormItem>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default SettingsFiscalCreateCertificateModal;
